/**
 * Dialog premium para enviar contato via WhatsApp
 * 
 * Permite ao usuário:
 * - Pesquisar contatos existentes do tenant
 * - Cadastrar novo contato manualmente com seletor de código de país
 * 
 * Design moderno com gradientes, bordas arredondadas e transições suaves
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Phone, Briefcase, Loader2, Search, UserPlus, Check, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getStorageUrl } from '@/lib/storage';
import { PhoneInput } from '@/components/ui/phone-input';

// Interface para contato buscado
interface SearchedContact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface SendContactDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
  /** Callback para enviar o contato */
  onSend: (contact: {
    name: string;
    phone: string;
    businessDescription?: string;
  }) => Promise<void>;
  /** ID do tenant para buscar contatos */
  tenantId?: string;
}

export function SendContactDialog({
  open,
  onClose,
  onSend,
  tenantId,
}: SendContactDialogProps) {
  // Estado da aba ativa
  const [activeTab, setActiveTab] = useState<'search' | 'manual'>('search');
  
  // Estados para pesquisa de contatos
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState<SearchedContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<SearchedContact | null>(null);
  
  // Estados do formulário manual (telefone completo gerenciado pelo PhoneInput)
  const [name, setName] = useState('');
  const [phone, setPhone] = useState(''); // Telefone completo com código de país
  const [businessDescription, setBusinessDescription] = useState('');
  
  // Estado de envio
  const [sending, setSending] = useState(false);

  // Validação do formulário manual (telefone já vem completo do PhoneInput)
  const isManualValid = name.trim().length > 0 && phone.replace(/\D/g, '').length >= 10;
  
  // Validação da pesquisa (contato selecionado com telefone)
  const isSearchValid = selectedContact && selectedContact.phone;

  // Busca contatos do tenant com debounce
  useEffect(() => {
    if (!tenantId || !open) return;
    
    const searchContacts = async () => {
      setLoadingContacts(true);
      try {
        let query = supabase
          .from('contacts')
          .select('id, name, phone, email, avatar_url')
          .eq('tenant_id', tenantId)
          .not('phone', 'is', null) // Apenas contatos com telefone
          .order('name', { ascending: true })
          .limit(50);

        // Aplica filtro de busca se houver texto
        if (searchQuery.trim()) {
          query = query.or(`name.ilike.%${searchQuery}%,phone.ilike.%${searchQuery}%`);
        }

        const { data, error } = await query;
        
        if (error) {
          console.error('[SendContactDialog] Error searching contacts:', error);
          return;
        }
        
        setContacts(data || []);
      } catch (error) {
        console.error('[SendContactDialog] Error:', error);
      } finally {
        setLoadingContacts(false);
      }
    };
    
    // Debounce de 300ms
    const timer = setTimeout(searchContacts, 300);
    return () => clearTimeout(timer);
  }, [tenantId, searchQuery, open]);

  // Limpa estados quando fecha o dialog
  useEffect(() => {
    if (!open) {
      setActiveTab('search');
      setSearchQuery('');
      setSelectedContact(null);
      setName('');
      setPhone('');
      setBusinessDescription('');
    }
  }, [open]);

  // Handler para envio (pesquisa ou manual)
  const handleSend = async () => {
    if (sending) return;

    try {
      setSending(true);
      
      if (activeTab === 'search' && selectedContact) {
        // Envia contato selecionado da pesquisa
        await onSend({
          name: selectedContact.name || 'Contato',
          phone: selectedContact.phone!,
          businessDescription: undefined,
        });
      } else if (activeTab === 'manual' && isManualValid) {
        // Envia contato cadastrado manualmente (telefone já vem completo do PhoneInput)
        await onSend({
          name: name.trim(),
          phone: phone,
          businessDescription: businessDescription.trim() || undefined,
        });
      }
      
      onClose();
    } catch (error) {
      console.error('[SendContactDialog] Error sending:', error);
    } finally {
      setSending(false);
    }
  };

  // Handler para tecla Enter no formulário manual
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && activeTab === 'manual' && isManualValid) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handler para fechar (limpa estados)
  const handleClose = () => {
    if (sending) return;
    onClose();
  };

  // Extrai iniciais do nome para o avatar
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Verifica se o botão de enviar deve estar habilitado
  const canSend = activeTab === 'search' ? isSearchValid : isManualValid;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent 
        className="sm:max-w-[480px]"
        onInteractOutside={(e) => sending && e.preventDefault()}
        onPointerDownOutside={(e) => sending && e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        {/* Header compacto com gradiente */}
        <DialogHeader className="space-y-2 pb-0">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 shadow-lg shadow-sky-500/25">
            <User className="h-6 w-6 text-white" />
          </div>
          <div className="text-center space-y-0.5">
            <DialogTitle className="text-lg font-semibold">
              Enviar Contato
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Selecione ou cadastre um contato
            </p>
          </div>
        </DialogHeader>

        {/* Tabs Compactas */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'search' | 'manual')}>
          <TabsList className="grid w-full grid-cols-2 p-1 h-10 bg-muted/50 rounded-lg">
            <TabsTrigger 
              value="search" 
              className="flex items-center gap-1.5 rounded-md h-full text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-sky-600 dark:data-[state=active]:text-sky-400 transition-all duration-200"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="font-medium">Pesquisar</span>
            </TabsTrigger>
            <TabsTrigger 
              value="manual" 
              className="flex items-center gap-1.5 rounded-md h-full text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-sky-600 dark:data-[state=active]:text-sky-400 transition-all duration-200"
            >
              <UserPlus className="h-3.5 w-3.5" />
              <span className="font-medium">Cadastrar</span>
            </TabsTrigger>
          </TabsList>

          {/* Aba Pesquisar Contatos */}
          <TabsContent value="search" className="space-y-3 mt-3">
            {/* Campo de busca compacto */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou telefone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 rounded-lg bg-muted/50 border-0 text-sm focus-visible:ring-2 focus-visible:ring-sky-500 transition-all"
                disabled={sending}
              />
            </div>

            {/* Lista de contatos compacta */}
            <ScrollArea className="h-[180px] rounded-lg border border-border/50 bg-muted/20">
              {loadingContacts ? (
                <div className="flex flex-col items-center justify-center h-full gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-sky-600 dark:text-sky-400" />
                  <p className="text-xs text-muted-foreground">Buscando...</p>
                </div>
              ) : contacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                  <User className="h-6 w-6" />
                  <p className="text-xs">
                    {searchQuery ? 'Nenhum contato encontrado' : 'Busque um contato'}
                  </p>
                </div>
              ) : (
                <div className="p-1.5 space-y-0.5">
                  {contacts.map((contact) => {
                    const isSelected = selectedContact?.id === contact.id;
                    return (
                      <button
                        key={contact.id}
                        onClick={() => setSelectedContact(isSelected ? null : contact)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all duration-200 text-left ${
                          isSelected
                            ? 'bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-900/30 dark:to-blue-900/30 border border-sky-400 shadow-sm'
                            : 'hover:bg-muted/70 border border-transparent'
                        }`}
                        disabled={sending}
                      >
                        <Avatar className="h-9 w-9 border border-background shadow-sm">
                          <AvatarImage 
                            src={contact.avatar_url ? getStorageUrl(contact.avatar_url) : undefined} 
                          />
                          <AvatarFallback className={cn("text-xs font-semibold", getAvatarColorClasses(contact.name))}>
                            {getInitials(contact.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-foreground">
                            {contact.name || 'Sem nome'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Aba Cadastrar Manualmente */}
          <TabsContent value="manual" className="space-y-3 mt-3">
            {/* Nome do contato */}
            <div className="space-y-1.5">
              <Label htmlFor="contact-name" className="text-xs font-medium flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                  <User className="h-3 w-3 text-sky-600 dark:text-sky-400" />
                </div>
                Nome do Contato
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="contact-name"
                placeholder="Ex: João Silva"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                autoFocus={activeTab === 'manual'}
                className="h-9 rounded-lg bg-muted/50 border-0 text-sm focus-visible:ring-2 focus-visible:ring-sky-500 transition-all"
              />
            </div>

            {/* Telefone com seletor de código de país */}
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone" className="text-xs font-medium flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Phone className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                </div>
                Telefone
                <span className="text-destructive">*</span>
              </Label>
              <PhoneInput
                id="contact-phone"
                value={phone}
                onChange={setPhone}
                placeholder="11 99999-9999"
                disabled={sending}
              />
            </div>

            {/* Descrição/Cargo (opcional) */}
            <div className="space-y-1.5">
              <Label htmlFor="contact-description" className="text-xs font-medium flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Briefcase className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                </div>
                Descrição/Cargo
                <span className="text-xs text-muted-foreground font-normal">(opcional)</span>
              </Label>
              <Input
                id="contact-description"
                placeholder="Ex: Gerente Comercial"
                value={businessDescription}
                onChange={(e) => setBusinessDescription(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                className="h-9 rounded-lg bg-muted/50 border-0 text-sm focus-visible:ring-2 focus-visible:ring-sky-500 transition-all"
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer Compacto */}
        <DialogFooter className="gap-2 sm:gap-2 pt-1">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={sending}
            className="rounded-lg h-9 text-sm"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend || sending}
            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-md shadow-sky-500/20 rounded-lg h-9 px-4 text-sm transition-all duration-300"
          >
            {sending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Enviar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
